<template>
  <q-page padding class="q-pb-xl">
    <portable-text v-if="data.body" :component="{}" :value="data.body" />
    <q-img v-if="data.image" :placeholder-src="placeholderUrl" :src="imageUrl">
      <template #loading>
        <q-spinner color="white" size="2em" />
      </template>
    </q-img>
    <q-list v-if="data.reviews">
      <q-item v-for="review in data.reviews" :key="review._key" class="q-mb-md">
        <q-item-section>
          <q-item-label>{{ review.title }}</q-item-label>
          <div class="q-pt-md">
            <portable-text v-if="review.body" :component="{}" :value="review.body" />
          </div>
          <q-item-label caption>{{ review.customer }}</q-item-label>
        </q-item-section>
      </q-item>
    </q-list>
  </q-page>
</template>

<script setup>
import { computed, watch } from 'vue'
import { keepPreviousData, useQuery } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
// import { useMeta } from "quasar";
import { queryPage } from 'src/api/sanity/queries/page'
// import { i18n } from "src/boot/i18n";
import { queryClient } from 'src/boot/vueQuery'
import { useDehydrateQueryClient } from 'src/composables/useDehydrateQueryClient'
import { useHydrateQueryClient } from 'src/composables/useHydrateQueryClient'
import { useQueryErrorHandler } from 'src/composables/useQueryErrorHandler'
import { useUpdateStoreFromQuery } from 'src/composables/useUpdateStoreFromQuery'
import { usePageStore } from 'src/stores/page'
import { PortableText } from '@portabletext/vue'
// import { usePreferencesStore } from "src/stores/preferences";
import { useResponsiveImageWidth } from 'src/composables/useResponsiveImageWidth'
import { urlFor } from 'src/boot/sanityUrlFor'

const QKEY = 'BasicPage'

defineOptions({
  name: QKEY,
  async preFetch({ store, currentRoute, ssrContext }) {
    const slug = currentRoute.params.slug
    const pageStore = usePageStore(store)
    try {
      await queryClient.prefetchQuery({
        queryKey: [QKEY, slug],
        queryFn: () => queryPage({ slug }),
      })
      pageStore.page = queryClient.getQueryData([QKEY, slug])
      useDehydrateQueryClient(ssrContext)
    } catch (e) {
      console.error('Failed to execute preFetch queryPage', e)
    }
  },
})

const responsiveWidth = useResponsiveImageWidth(960)
// const { t } = i18n.global;
const router = useRouter()
const pageStore = usePageStore()
const { page } = storeToRefs(pageStore)
// const preferencesStore = usePreferencesStore();
// const { motion } = storeToRefs(preferencesStore);
// const motionRef = ref(null);
// const motionInstance = ref(null);
// const { setupMotion } = useFromLeft(motionRef);

const slug = computed(() => router.currentRoute.value.params.slug)
const queryKey = computed(() => [QKEY, slug.value])
const variables = computed(() => {
  return { slug: slug.value }
})

// const pageComponent = (slug) => {
//   switch (slug) {
//     case "contact":
//       return ContactForm;
//     case "pricing":
//       return PricingForm;
//     case "services":
//       return ServicesList;
//     default:
//       break;
//   }
// };

const { data, error, isError, refetch } = useQuery({
  queryKey: [queryKey.value],
  queryFn: () => queryPage(variables.value),
  initialData: page?.value,
  placeholderData: keepPreviousData,
})

// const metaDescription = t("meta.description");
// const metaImage = t("meta.image");
// const metaTitle = computed(() => `${t("meta.title")} - ${data?.value?.title}`);

// useMeta(() => {
//   return {
//     meta: {
//       description: {
//         name: "description",
//         content: metaDescription,
//       },
//       ogDescription: {
//         property: "og:description",
//         content: metaDescription,
//       },
//       ogTitle: {
//         property: "og:title",
//         content: metaTitle.value,
//       },
//       ogImage: {
//         property: "og:image",
//         content: metaImage,
//       },
//     },
//     title: metaTitle.value,
//   };
// });

// onMounted(async () => {
//   if (motion.value) {
//     await nextTick();
//     if (motionRef.value) {
//       motionInstance.value = setupMotion();
//       motionInstance.value.apply("enter");
//     }
//   }
// });

watch(
  queryKey,
  () => {
    refetch()
  },
  { deep: true },
)

useQueryErrorHandler(error, isError)
useUpdateStoreFromQuery(data, page)
useHydrateQueryClient()

const imageUrl = computed(() =>
  urlFor(data.value?.image?.asset?.url).width(responsiveWidth.value).url(),
)
const placeholderUrl = computed(() => data.value?.image?.asset?.metadata?.lqip)
</script>
